// ============================================================
// useAuth — SSO login/logout/session composable
// ============================================================
import { ref, computed, onMounted } from 'vue';
import type { SessionInfo } from '../../shared/types';
import { fetchSession, getLoginUrl, logout as apiLogout } from './useApi';

const session = ref<SessionInfo | null>(null);
const loading = ref(false);

export function useAuth() {
  const isLoggedIn = computed(() => session.value?.isLoggedIn ?? false);
  const characterName = computed(() => session.value?.characterName ?? null);
  const characterId = computed(() => session.value?.characterId ?? null);

  async function checkSession(): Promise<void> {
    loading.value = true;
    try {
      session.value = await fetchSession();
    } catch {
      session.value = { isLoggedIn: false };
    } finally {
      loading.value = false;
    }
  }

  async function login(): Promise<void> {
    try {
      const url = await getLoginUrl();
      window.location.href = url;
    } catch {
      // Toast already shown by useApi
    }
  }

  async function logout(): Promise<void> {
    try {
      await apiLogout();
      session.value = { isLoggedIn: false };
    } catch {
      // Toast already shown by useApi
    }
  }

  onMounted(() => {
    if (!session.value) {
      checkSession();
    }
  });

  return {
    session,
    loading,
    isLoggedIn,
    characterName,
    characterId,
    checkSession,
    login,
    logout,
  };
}
